import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  api,
  ApiError,
  type ActivityRow,
  type CommentRow,
  type DocumentDetail,
  type VersionDiff,
  type VersionSummary,
  type AssignmentRow,
  type AssignmentsResponse,
  type ReviewDecisionResponse,
} from "../../lib/api";
import { useUser } from "../../lib/auth";
import { NotFoundScreen } from "../errors/NotFoundScreen";
import { formatBytes, formatDateTime, timeAgo } from "../../lib/format";
import {
  Avatar,
  Banner,
  Card,
  Empty,
  ErrorNote,
  PageHeader,
  ReviewBadge,
  Spinner,
  StatusPill,
  Tabs,
  useToast,
} from "../../components/ui";

const TABS = [
  { id: "preview", label: "Preview" },
  { id: "versions", label: "Versions" },
  { id: "comments", label: "Comments" },
  { id: "workflow", label: "Workflow" },
  { id: "activity", label: "Activity" },
];

const DECISION_TONE: Record<string, { bg: string; fg: string; word: string }> = {
  PENDING: { bg: "#EFECEE", fg: "#57515B", word: "waiting" },
  APPROVED: { bg: "#E6F7DC", fg: "#2F6420", word: "approved" },
  CHANGES_REQUESTED: { bg: "#FFECEC", fg: "#B23438", word: "changes requested" },
  REJECTED: { bg: "#FFECEC", fg: "#B23438", word: "rejected" },
};

/**
 * Who was asked, and what they said.
 *
 * The count in the header is the thing an author actually wants: a document in
 * review is not waiting on "a manager", it is waiting on the two of five people
 * who have not answered yet, and until now there was nowhere to see that.
 */
function ReviewPanel({ documentId }: { documentId: string }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ["assignments", documentId],
    queryFn: () =>
      api.get<AssignmentsResponse>(`/workflow/documents/${documentId}/assignments`),
  });

  const decide = useMutation({
    mutationFn: (decision: string) => {
      const comment =
        decision === "APPROVED"
          ? undefined
          : window.prompt("What needs to change?")?.trim();
      if (decision !== "APPROVED" && !comment) {
        throw new Error("A reason is required when returning a document.");
      }
      return api.post<ReviewDecisionResponse>(
        `/workflow/documents/${documentId}/review`,
        { decision, comment },
      );
    },
    onSuccess: (result) => {
      toast(result.message);
      void queryClient.invalidateQueries({ queryKey: ["assignments", documentId] });
      void queryClient.invalidateQueries({ queryKey: ["document", documentId] });
    },
    onError: (caught) =>
      toast(
        caught instanceof Error ? caught.message : "Could not record that",
        "danger",
      ),
  });

  if (!data || data.total === 0) return null;

  const person = (row: AssignmentRow) => {
    const tone = DECISION_TONE[row.decision] ?? DECISION_TONE.PENDING;
    return (
      <li
        key={row.userId}
        className="flex items-start gap-2.5 border-b border-line-soft px-3 py-2.5 last:border-0"
      >
        <Avatar initials={row.initials} colour={row.avatarColour} size={26} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-base font-bold text-ink">
            {row.displayName}
          </span>
          {row.comment && (
            <span className="mt-0.5 block text-2xs leading-relaxed text-muted">
              {row.comment}
            </span>
          )}
        </span>
        <span className="pill shrink-0" style={{ background: tone.bg, color: tone.fg }}>
          {tone.word}
        </span>
      </li>
    );
  };

  return (
    <Card
      className="mt-4"
      title="Review"
      meta={`${data.approved} of ${data.total} approved · round ${data.roundNo}`}
      padded={false}
    >
      <ul>{data.reviewers.map(person)}</ul>
      {data.approver && (
        <>
          <div className="border-t border-line-soft bg-surface-subtle px-3 py-1.5">
            <span className="eyebrow !text-muted">Final approver</span>
          </div>
          <ul>{person(data.approver)}</ul>
        </>
      )}
      {data.myDecision === "PENDING" && (
        <div className="flex flex-wrap gap-2 border-t border-line-soft px-3 py-2.5">
          <button
            className="btn-accent !py-1.5"
            disabled={decide.isPending}
            onClick={() => decide.mutate("APPROVED")}
          >
            Approve
          </button>
          <button
            className="btn-ghost !py-1.5"
            disabled={decide.isPending}
            onClick={() => decide.mutate("CHANGES_REQUESTED")}
          >
            Request changes
          </button>
          <button
            className="btn-ghost !py-1.5"
            disabled={decide.isPending}
            onClick={() => decide.mutate("REJECTED")}
          >
            Reject
          </button>
        </div>
      )}
    </Card>
  );
}

export function DocumentDetailScreen() {
  const { documentId = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const user = useUser();
  const queryClient = useQueryClient();

  const tab = params.get("tab") ?? "preview";
  const page = Number(params.get("page") ?? 1);
  const [comment, setComment] = useState("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["document", documentId],
    queryFn: () => api.get<DocumentDetail>(`/documents/${documentId}`),
  });

  const setTab = (next: string) => {
    const updated = new URLSearchParams(params);
    updated.set("tab", next);
    setParams(updated, { replace: true });
  };

  const setPage = (next: number) => {
    const updated = new URLSearchParams(params);
    updated.set("page", String(next));
    setParams(updated, { replace: true });
  };

  const transition = useMutation({
    mutationFn: (payload: { action: string; comment?: string }) =>
      api.post<{ message: string }>(
        `/workflow/documents/${documentId}/transition`,
        payload,
      ),
    onSuccess: (result) => {
      toast(result.message);
      void queryClient.invalidateQueries({ queryKey: ["document", documentId] });
      void queryClient.invalidateQueries({ queryKey: ["documents"] });
    },
    onError: (caught) =>
      toast(caught instanceof Error ? caught.message : "Action refused", "danger"),
  });

  const lock = useMutation({
    mutationFn: (action: "checkout" | "checkin") =>
      api.post<{ message: string }>(`/documents/${documentId}/${action}`),
    onSuccess: (result) => {
      toast(result.message);
      void queryClient.invalidateQueries({ queryKey: ["document", documentId] });
    },
    onError: (caught) =>
      toast(caught instanceof Error ? caught.message : "Could not change lock", "danger"),
  });

  if (isLoading) return <Spinner />;

  // A document that is missing, archived away, or outside this reader's
  // permission is a routine outcome of following an old link, not a fault. It
  // gets the not-found page and somewhere to go next; a raw error banner
  // leaves the reader on a dead screen with a stack-trace-shaped message.
  //
  // 403 and 404 deliberately read the same. Section 7 trims restricted
  // documents out of retrieval entirely, and distinguishing "you may not see
  // this" from "this does not exist" would confirm the document exists to
  // someone not permitted to know that.
  if (error instanceof ApiError && (error.status === 404 || error.status === 403)) {
    return (
      <NotFoundScreen
        title="That document is not available"
        detail="It may have been archived or removed, or it may be restricted to a group you are not in. Published documents are always reachable from the library."
      />
    );
  }
  if (error) return <ErrorNote error={error} />;
  if (!data) return null;

  const runAction = (action: string, requiresComment: boolean) => {
    let note: string | undefined;
    if (requiresComment) {
      // The engine rejects a change request with no comment, so ask before
      // sending rather than surfacing a validation error.
      const entered = window.prompt(
        "A comment is required when requesting changes. What needs to change?",
      );
      if (!entered?.trim()) return;
      note = entered.trim();
    }
    transition.mutate({ action, comment: note });
  };

  return (
    <>
      <button
        onClick={() => navigate("/documents")}
        className="btn-quiet mb-3 !px-0 !text-sm"
      >
        ← Back to Document List
      </button>

      <PageHeader
        eyebrow={`${data.docNo} · ${data.versionLabel} · ${data.currentVersion?.pageCount ?? 0} pages`}
        title={data.title}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <StatusPill status={data.status} label={data.statusLabel} />
            <ReviewBadge state={data.reviewState} days={data.daysUntilReview} />
            <span>
              Owner {data.owner?.displayName ?? "unassigned"} · {data.accessLabel} ·{" "}
              {data.retentionLabel}
            </span>
          </span>
        }
        actions={
          <>
            {data.availableActions.map((action) =>
              /* Submitting means naming people, so it is a form rather than a
                 button. The server refuses a bare submit for the same reason. */
              action.needsRoster ? (
                <Link
                  key={action.action}
                  to={`/documents/${documentId}/submit`}
                  className="btn-accent"
                >
                  {action.label}
                </Link>
              ) : (
                <button
                  key={action.action}
                  className={
                    action.action.startsWith("APPROVE") ? "btn-accent" : "btn-ghost"
                  }
                  disabled={transition.isPending}
                  onClick={() => runAction(action.action, action.requiresComment)}
                >
                  {action.label}
                </button>
              ),
            )}
            {data.canEditContent && (
              <Link
                to={`/documents/${documentId}/edit`}
                className="btn-accent no-underline"
              >
                Edit document
              </Link>
            )}
            {data.canCheckOut && (
              <button
                className="btn-ghost"
                disabled={lock.isPending}
                onClick={() => {
                  if (
                    !data.lock?.isMine &&
                    data.status === "PUBLISHED" &&
                    !window.confirm(
                      "Checking out starts a new version as a draft. Only you can edit it until you submit it. The published version stays in force, and the assistant keeps answering from it, until your changes are approved. Continue?",
                    )
                  )
                    return;
                  lock.mutate(data.lock?.isMine ? "checkin" : "checkout");
                }}
              >
                {data.lock?.isMine ? "Check in" : "Check out"}
              </button>
            )}
            {data.webUrl && (
              <a
                href={data.webUrl}
                target="_blank"
                rel="noreferrer"
                className="btn-ghost no-underline"
              >
                Open in SharePoint
              </a>
            )}
            <Link
              to={`/assistant?q=${encodeURIComponent(`What does ${data.docNo} say about ${data.title.toLowerCase()}?`)}`}
              className="btn-ghost no-underline"
            >
              Ask AI
            </Link>
            <button
              className="btn-ghost"
              onClick={() =>
                void api
                  .blob(`/documents/${documentId}/content`)
                  .then((url) => {
                    const anchor = document.createElement("a");
                    anchor.href = url;
                    anchor.download = data.sourceFilename || `${data.docNo}.pdf`;
                    anchor.click();
                    URL.revokeObjectURL(url);
                  })
                  .catch(() => toast("Could not download", "danger"))
              }
            >
              Download
            </button>
          </>
        }
      />

      {data.conflict && (
        <Banner
          tone="danger"
          action={
            user.canReview ? (
              <span className="flex gap-2">
                <button
                  className="btn-ghost !py-1"
                  onClick={() =>
                    void api
                      .post(`/admin/conflicts/${data.conflict!.id}/PULL_SOURCE`)
                      .then((r) => {
                        toast((r as { message: string }).message);
                        void queryClient.invalidateQueries({
                          queryKey: ["document", documentId],
                        });
                      })
                  }
                >
                  Pull source version
                </button>
                <button
                  className="btn-ghost !py-1"
                  onClick={() =>
                    void api
                      .post(`/admin/conflicts/${data.conflict!.id}/KEEP_LOCAL`)
                      .then((r) => {
                        toast((r as { message: string }).message);
                        void queryClient.invalidateQueries({
                          queryKey: ["document", documentId],
                        });
                      })
                  }
                >
                  Keep this version
                </button>
              </span>
            ) : undefined
          }
        >
          <strong>Changed at source.</strong> This document was modified in the
          library while a review was open. Pull the source version to re-ingest
          and restart the stage, or keep this one and overwrite the source on
          publish. Both decisions are audited.
        </Banner>
      )}

      {data.lock && !data.lock.isMine && (
        <Banner tone="warn">
          Checked out to {data.lock.heldBy.displayName}. Only they can edit or
          submit it until it is checked in or submitted.
        </Banner>
      )}

      {data.publishedVersionId &&
        data.currentVersion &&
        data.publishedVersionId !== data.currentVersion.id && (
          <Banner tone="info">
            <strong>Revision in progress.</strong> You are looking at{" "}
            {data.currentVersion.versionLabel}, a draft. The published version
            stays in force, and is what the assistant answers from, until this
            draft is approved. Approval also updates the file in SharePoint.
          </Banner>
        )}

      <Tabs tabs={TABS} active={tab} onChange={setTab} />

      {tab === "preview" && (
        <PreviewTab
          document={data}
          documentId={documentId}
          page={page}
          onPage={setPage}
        />
      )}
      {tab === "versions" && (
        <VersionsTab documentId={data.id} versions={data.versions} />
      )}
      {tab === "comments" && (
        <CommentsTab
          documentId={documentId}
          canComment={data.canComment}
          comment={comment}
          setComment={setComment}
        />
      )}
      {tab === "workflow" && <WorkflowTab document={data} />}
      {tab === "activity" && <ActivityTab documentId={documentId} />}
    </>
  );
}

/**
 * The reader.
 *
 * Shows server-rendered page images rather than embedding pdf.js. For a proof
 * of concept this is the smaller moving part: the backend already renders and
 * caches every page, every format looks the same because everything is
 * converted to PDF first, and there is no worker bundle to configure. The
 * trade-off is that text is not selectable in the reader, which is the reason
 * to add pdf.js later.
 */
function PreviewTab({
  document: doc,
  documentId,
  page,
  onPage,
}: {
  document: DocumentDetail;
  documentId: string;
  page: number;
  onPage: (page: number) => void;
}) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [thumbs, setThumbs] = useState<Record<number, string>>({});
  const [find, setFind] = useState("");
  const [hits, setHits] = useState<number[] | null>(null);
  const [failed, setFailed] = useState(false);

  const pageCount = doc.currentVersion?.pageCount ?? 1;
  const safePage = Math.min(Math.max(page, 1), pageCount);

  useEffect(() => {
    let revoked: string | null = null;
    setFailed(false);
    void api
      .blob(`/documents/${documentId}/pages/${safePage}`)
      .then((url) => {
        revoked = url;
        setImageUrl(url);
      })
      .catch(() => setFailed(true));
    return () => {
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [documentId, safePage]);

  useEffect(() => {
    // Thumbnails for the strip, capped so a 400-page document does not fire
    // four hundred requests on open.
    const wanted = Array.from({ length: Math.min(pageCount, 12) }, (_, i) => i + 1);
    let cancelled = false;
    void Promise.all(
      wanted.map(async (n) => {
        try {
          return [n, await api.blob(`/documents/${documentId}/pages/${n}`, { thumbnail: true })] as const;
        } catch {
          return [n, ""] as const;
        }
      }),
    ).then((entries) => {
      if (cancelled) return;
      setThumbs(Object.fromEntries(entries.filter(([, url]) => url)));
    });
    return () => {
      cancelled = true;
    };
  }, [documentId, pageCount]);

  const runFind = async () => {
    if (!find.trim()) return setHits(null);
    const result = await api.get<{ pages: number[]; hitCount: number }>(
      `/documents/${documentId}/find`,
      { q: find },
    );
    setHits(result.pages);
    if (result.pages.length > 0) onPage(result.pages[0]);
  };

  return (
    <div className="grid gap-4 xl:grid-cols-[150px_1fr_280px]">
      <div className="hidden max-h-[70vh] space-y-2 overflow-y-auto xl:block">
        {Array.from({ length: pageCount }, (_, i) => i + 1).map((n) => (
          <button
            key={n}
            onClick={() => onPage(n)}
            className={`block w-full overflow-hidden rounded-card border p-1 transition-colors ${
              n === safePage ? "border-ink" : "border-line hover:border-muted-soft"
            }`}
          >
            {thumbs[n] ? (
              <img src={thumbs[n]} alt={`Page ${n}`} className="w-full" />
            ) : (
              <div className="grid h-24 place-items-center bg-surface-sunken text-2xs text-muted-soft">
                {n}
              </div>
            )}
            <span className="mt-1 block text-2xs text-muted-soft">{n}</span>
          </button>
        ))}
      </div>

      <Card padded={false}>
        <div className="flex flex-wrap items-center gap-2 border-b border-line-soft px-3 py-2">
          <button
            className="btn-ghost !py-1"
            disabled={safePage <= 1}
            onClick={() => onPage(safePage - 1)}
          >
            ‹
          </button>
          <span className="text-sm font-bold text-muted-strong">
            Page {safePage} of {pageCount}
          </span>
          <button
            className="btn-ghost !py-1"
            disabled={safePage >= pageCount}
            onClick={() => onPage(safePage + 1)}
          >
            ›
          </button>

          <input
            value={find}
            onChange={(event) => setFind(event.target.value)}
            onKeyDown={(event) => event.key === "Enter" && void runFind()}
            placeholder="Find in document"
            aria-label="Find in document"
            className="field ml-auto max-w-[220px] !py-1"
          />
          <button className="btn-ghost !py-1" onClick={() => void runFind()}>
            Find
          </button>
          {hits !== null && (
            <span className="text-2xs font-bold text-muted-soft">
              {hits.length === 0
                ? "no pages match"
                : `${hits.length} page${hits.length === 1 ? "" : "s"}: ${hits.join(", ")}`}
            </span>
          )}
        </div>

        <div className="grid min-h-[60vh] place-items-center bg-surface-sunken p-4">
          {failed ? (
            <Empty
              title="No page image yet"
              detail="The document may still be processing, or the rendition failed. The Ingestion screen shows the job state."
            />
          ) : imageUrl ? (
            <img
              src={imageUrl}
              alt={`${doc.title}, page ${safePage}`}
              className="max-w-full rounded border border-line bg-white shadow-sm"
            />
          ) : (
            <Spinner label="Rendering page…" />
          )}
        </div>
      </Card>

      <div className="space-y-4">
        <ReviewPanel documentId={documentId} />

      <Card title="Properties" collapsible storageKey="properties" defaultOpen={false}>
          <dl className="space-y-2">
            {doc.properties.map((property) => (
              <div key={property.key} className="flex gap-3 text-sm">
                <dt className="w-32 shrink-0 text-muted-soft">{property.key}</dt>
                <dd className="min-w-0 flex-1 break-words font-medium text-muted-strong">
                  {property.value}
                </dd>
              </div>
            ))}
          </dl>
        </Card>

        {doc.currentVersion && (
          <Card title="Indexing" collapsible storageKey="indexing" defaultOpen={false}>
            <dl className="space-y-2 text-sm">
              <Row
                label="Extractor"
                value={doc.currentVersion.extractorName || "—"}
              />
              <Row
                label="OCR"
                value={
                  doc.currentVersion.ocrApplied
                    ? `Applied${
                        doc.currentVersion.ocrConfidence
                          ? ` · ${Math.round(doc.currentVersion.ocrConfidence * 100)}% confidence`
                          : ""
                      }`
                    : "Not needed"
                }
              />
              <Row
                label="Passages"
                value={String(doc.currentVersion.chunkCount)}
              />
              <Row
                label="Indexed"
                value={
                  doc.currentVersion.indexedAt
                    ? timeAgo(doc.currentVersion.indexedAt)
                    : "not indexed"
                }
              />
              <Row
                label="Retrievable"
                value={doc.currentVersion.chunkCount > 0 ? "Yes" : "No"}
              />
            </dl>
            {doc.currentVersion.ocrNeedsCheck && (
              <p
                className="mt-3 rounded-card px-3 py-2 text-2xs"
                style={{ background: "#FCF6D2", color: "#75650B" }}
              >
                Some pages were transcribed with low confidence. Check them before
                relying on a citation from this document.
              </p>
            )}
            {doc.currentVersion.warnings.length > 0 && (
              <ul className="mt-3 space-y-1 border-t border-line-soft pt-2 text-2xs text-muted-soft">
                {doc.currentVersion.warnings.map((warning, index) => (
                  <li key={index}>{warning}</li>
                ))}
              </ul>
            )}
          </Card>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-3">
      <dt className="w-28 shrink-0 text-muted-soft">{label}</dt>
      <dd className="font-medium text-muted-strong">{value}</dd>
    </div>
  );
}

function VersionsTab({
  documentId,
  versions,
}: {
  documentId: string;
  versions: VersionSummary[];
}) {
  // Defaults to the change that produced the version in force, which is the
  // comparison anyone opening this tab is nearly always after.
  const [from, setFrom] = useState(versions[1]?.id ?? "");
  const [to, setTo] = useState(versions[0]?.id ?? "");

  const {
    data: diff,
    isFetching,
    error: diffError,
  } = useQuery({
    queryKey: ["diff", documentId, from, to],
    queryFn: () =>
      api.get<VersionDiff>(`/documents/${documentId}/diff`, { from, to }),
    enabled: Boolean(from && to && from !== to),
  });

  if (versions.length === 0) {
    return (
      <Card>
        <Empty title="No versions yet" />
      </Card>
    );
  }
  return (
    <>
    <Card padded={false}>
      <ul>
        {versions.map((version) => (
          <li
            key={version.id}
            className="flex flex-wrap items-center gap-3 border-b border-line-soft px-4 py-3 last:border-0"
          >
            <span className="font-display text-[19px] text-ink">
              {version.versionLabel}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-base text-ink">
                {version.changeNote || "No change note"}
              </span>
              <span className="text-2xs text-muted-soft">
                {formatDateTime(version.createdAt)} · {formatBytes(version.size)} ·{" "}
                {version.pageCount} pages · {version.chunkCount} passages
              </span>
            </span>
            {version.isPublished && (
              <span className="pill" style={{ background: "#E6F7DC", color: "#2F6420" }}>
                Published
              </span>
            )}
            {version.isCurrent && !version.isPublished && (
              <span className="pill" style={{ background: "#FFF4DC", color: "#7A5300" }}>
                {version.origin === "draft" ? "Draft" : "Working copy"}
              </span>
            )}
            {version.isArchived && (
              <span className="pill" style={{ background: "#EFECEE", color: "#57515B" }}>
                Archived
              </span>
            )}
          </li>
        ))}
      </ul>
      <p className="border-t border-line-soft px-4 py-2.5 text-2xs text-muted-soft">
        Superseded versions are archived on publish. They stay retrievable so you
        can prove what was in force on a given date, but are excluded from
        answers unless the archived scope is enabled.
      </p>
    </Card>

    {/* The compare endpoint has been there since the versions were; nothing on
        this screen reached it, so a reader could see that a version existed and
        not what it changed -- which is most of the reason to keep one. */}
    {versions.length > 1 && (
      <Card
        className="mt-4"
        title="Compare versions"
        action={
          <div className="flex items-center gap-1.5">
            <VersionSelect
              label="Compare from"
              value={from}
              onChange={setFrom}
              versions={versions}
            />
            <span className="text-sm text-muted-soft">to</span>
            <VersionSelect
              label="Compare to"
              value={to}
              onChange={setTo}
              versions={versions}
            />
          </div>
        }
      >
        {from === to ? (
          <p className="py-6 text-center text-base+ text-muted">
            Pick two different versions.
          </p>
        ) : isFetching ? (
          <Spinner label="Comparing…" />
        ) : diffError ? (
          <ErrorNote error={diffError} />
        ) : diff ? (
          <>
            <p className="mb-3 text-base+ text-muted">
              <strong className="text-ink">{diff.fromLabel}</strong> to{" "}
              <strong className="text-ink">{diff.toLabel}</strong> ·{" "}
              <span className="font-bold text-accent-deep">
                +{diff.additions}
              </span>{" "}
              <span className="font-bold text-danger">−{diff.removals}</span>{" "}
              passages
            </p>
            {diff.lines.length === 0 ? (
              <p className="py-4 text-center text-base+ text-muted">
                The extracted text is identical.
              </p>
            ) : (
              <ol className="max-h-[420px] overflow-auto rounded-field border border-line">
                {diff.lines.map((line, index) => (
                  <li
                    key={index}
                    className={`flex gap-2 border-b border-line-soft px-3 py-2 last:border-0 ${
                      line.mark === "+"
                        ? "bg-accent-wash"
                        : line.mark === "-"
                          ? "bg-danger-wash"
                          : ""
                    }`}
                  >
                    <span
                      className={`w-3 shrink-0 select-none text-center font-mono text-sm font-bold ${
                        line.mark === "+"
                          ? "text-accent-deep"
                          : line.mark === "-"
                            ? "text-danger"
                            : "text-muted-faint"
                      }`}
                      aria-hidden
                    >
                      {line.mark || "·"}
                    </span>
                    {/* Whitespace preserved: a passage carries the line breaks
                        of the page it came from, and flattening them makes two
                        different passages look the same. */}
                    <span className="min-w-0 flex-1 whitespace-pre-wrap break-words text-sm leading-[1.6] text-prose">
                      {line.text}
                    </span>
                  </li>
                ))}
              </ol>
            )}
            <p className="mt-2.5 text-2xs leading-relaxed text-muted-soft">
              Compared on extracted text, not the original file, so this shows
              what changed for search and for answers. A version whose only
              change was formatting will show nothing.
            </p>
          </>
        ) : null}
      </Card>
    )}
    </>
  );
}

function VersionSelect({
  label,
  value,
  onChange,
  versions,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  versions: VersionSummary[];
}) {
  return (
    <select
      aria-label={label}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="rounded-nav border border-line bg-surface px-2 py-1 text-base font-bold text-ink focus:border-muted-soft focus:outline-none"
    >
      {versions.map((version) => (
        <option key={version.id} value={version.id}>
          {version.versionLabel}
        </option>
      ))}
    </select>
  );
}

function CommentsTab({
  documentId,
  canComment,
  comment,
  setComment,
}: {
  documentId: string;
  canComment: boolean;
  comment: string;
  setComment: (value: string) => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const { data, isLoading } = useQuery({
    queryKey: ["comments", documentId],
    queryFn: () => api.get<CommentRow[]>(`/documents/${documentId}/comments`),
  });

  const add = useMutation({
    mutationFn: (body: string) =>
      api.post(`/documents/${documentId}/comments`, { body }),
    onSuccess: () => {
      setComment("");
      toast("Comment added and the owner notified");
      void queryClient.invalidateQueries({ queryKey: ["comments", documentId] });
    },
    onError: (error) =>
      toast(error instanceof Error ? error.message : "Could not comment", "danger"),
  });

  return (
    <Card>
      {isLoading && <Spinner />}
      {data?.length === 0 && (
        <p className="py-6 text-center text-sm text-muted">
          No comments on this document yet.
        </p>
      )}
      <ul className="space-y-3">
        {data?.map((entry) => (
          <li key={entry.id} className="flex gap-3">
            <Avatar
              initials={entry.author?.initials ?? "?"}
              colour={entry.author?.avatarColour ?? "#DDD7DA"}
              size={28}
            />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="text-base font-bold text-ink">
                  {entry.author?.displayName ?? "Removed user"}
                </span>
                {entry.authorRole && (
                  <span className="rounded-chip bg-surface-sunken px-1.5 py-[1px] text-2xs font-bold text-muted">
                    {entry.authorRole}
                  </span>
                )}
                {entry.isChangeRequest && (
                  <span
                    className="pill"
                    style={{ background: "#FFECEC", color: "#B23438" }}
                  >
                    Change request
                  </span>
                )}
                <span className="text-2xs text-muted-soft">
                  {formatDateTime(entry.createdAt)}
                  {entry.versionLabel && ` · against ${entry.versionLabel}`}
                </span>
              </div>
              <p className="mt-1 text-base leading-relaxed text-muted-strong">
                {entry.body}
              </p>
            </div>
          </li>
        ))}
      </ul>

      <div className="mt-4 border-t border-line-soft pt-4">
        {canComment ? (
          <div className="flex gap-2">
            <input
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              onKeyDown={(event) =>
                event.key === "Enter" && comment.trim() && add.mutate(comment.trim())
              }
              placeholder="Add a review comment…"
              aria-label="Add a comment"
              className="field"
            />
            <button
              className="btn-primary"
              disabled={!comment.trim() || add.isPending}
              onClick={() => add.mutate(comment.trim())}
            >
              Comment
            </button>
          </div>
        ) : (
          <p className="text-sm text-muted">
            Your role has read-only access to this document.
          </p>
        )}
        <p className="mt-2 text-2xs text-muted-soft">
          Comments are attached to the version they were written against and are
          recorded in the audit log.
        </p>
      </div>
    </Card>
  );
}

function WorkflowTab({ document: doc }: { document: DocumentDetail }) {
  const stages = [
    { label: "Draft", status: "WORK_IN_PROGRESS" },
    { label: "Review", status: "AWAITING_REVIEW" },
    { label: "Approval", status: "AWAITING_APPROVAL" },
    { label: "Publish", status: "PUBLISHED" },
  ];
  const order = ["WORK_IN_PROGRESS", "AWAITING_REVIEW", "AWAITING_APPROVAL", "PUBLISHED"];
  const current = order.indexOf(doc.status);

  return (
    <Card title="Approval route" collapsible storageKey="approval-route" defaultOpen={false}>
      <ol className="space-y-3">
        {stages.map((stage, index) => {
          const done = current > index;
          const active = current === index;
          return (
            <li key={stage.label} className="flex items-start gap-3">
              <span
                className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full"
                style={{
                  background: done ? "#8DE971" : active ? "#AD96DC" : "#DDD7DA",
                }}
              />
              <div>
                <div className="text-base font-bold text-ink">
                  {index + 1}. {stage.label}
                  {active && (
                    <span className="ml-2 text-2xs font-extrabold uppercase tracking-wider text-[#4B2E83]">
                      current
                    </span>
                  )}
                </div>
                <div className="text-sm text-muted">
                  {index < 2 ? "Network Control Engineer" : "Network Control Manager"}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
      <p className="mt-4 border-t border-line-soft pt-3 text-2xs leading-relaxed text-muted-soft">
        Publishing writes back to the library and re-indexes the document
        immediately rather than waiting for the next scheduled sync. If the
        connector is offline, an approval is queued and publishes automatically
        when the connection returns.
      </p>
    </Card>
  );
}

function ActivityTab({ documentId }: { documentId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ["activity", documentId],
    queryFn: () => api.get<ActivityRow[]>(`/documents/${documentId}/activity`),
  });

  if (isLoading) return <Spinner />;
  if (!data || data.length === 0) {
    return (
      <Card>
        <Empty title="No recorded activity yet" />
      </Card>
    );
  }

  return (
    <Card padded={false}>
      <ul>
        {data.map((row) => (
          <li
            key={row.id}
            className="flex items-start gap-3 border-b border-line-soft px-4 py-3 last:border-0"
          >
            <Avatar initials={row.initials} colour="#EFECEE" size={24} />
            <div className="min-w-0 flex-1">
              <div className="text-base text-ink">{row.summary}</div>
              <div className="text-2xs text-muted-soft">
                {row.type} · {formatDateTime(row.occurredAt)}
              </div>
            </div>
          </li>
        ))}
      </ul>
      <p className="border-t border-line-soft px-4 py-2.5 text-2xs text-muted-soft">
        Activity is read from the audit log, so it is the same record that is
        retained for seven years and exported to CSV.
      </p>
    </Card>
  );
}
